import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faUserTie,
  faMapMarkerAlt,
  faBuilding,
  faHome,
  faSearch,
  faFilter,
  faArchive,
  faAddressBook,
  faUserPlus,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EmployeeContactList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const navigate = useNavigate();
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${BASE_URL}/api/employees`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error fetching employees");
        }

        const data = await response.json();
        const activeEmployees = data.filter((employee) => !employee.archive);
        setEmployees(activeEmployees);
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Error fetching employees");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const handleArchive = async (employeeId) => {
    const confirmArchive = window.confirm(
      "Are you sure you want to archive this employee?"
    );
    if (confirmArchive) {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(
          `${BASE_URL}/api/employees/${employeeId}/archive`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || errorData.msg || "Error archiving employee"
          );
        }

        setEmployees(
          employees.filter((employee) => employee._id !== employeeId)
        );
      } catch (err) {
        console.error("Error archiving employee:", err);
        alert("Error archiving employee");
      }
    }
  };

  const filteredEmployees = employees.filter((employee) => {
    if (!filterQuery) return true;
    switch (filterType) {
      case "employeeid":
        return employee.employeeid
          ?.toLowerCase()
          .includes(filterQuery.toLowerCase());
      case "name":
        return employee.name?.toLowerCase().includes(filterQuery.toLowerCase());
      case "contact number":
        return employee.contactNumber?.includes(filterQuery);
      case "email":
        return employee.email
          ?.toLowerCase()
          .includes(filterQuery.toLowerCase());
      case "managerID":
        return employee.manager
          ?.toLowerCase()
          .includes(filterQuery.toLowerCase());
      case "managerName":
        return employee.managerName
          ?.toLowerCase()
          .includes(filterQuery.toLowerCase());
      case "job location":
        return employee.jobLocation
          ?.toLowerCase()
          .includes(filterQuery.toLowerCase());
      case "role":
        return employee.role?.toLowerCase().includes(filterQuery.toLowerCase());
      default:
        return true;
    }
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterQuery]);

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const currentEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const handleNavigation = () => {
    navigate("/create-employee");
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Contact Directory"
        subtitle="Search and view employee contact information"
        icon={<FontAwesomeIcon icon={faAddressBook} />}
        actions={
          <Button
            variant="contained"
            size="small"
            onClick={handleNavigation}
            startIcon={<FontAwesomeIcon icon={faUserPlus} style={{ fontSize: 13 }} />}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 2.5,
              py: 1,
              boxShadow: "0 1px 3px rgba(102,126,234,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
            }}
          >
            Register Employee
          </Button>
        }
      />

      {/* Table Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Search & Filter Bar */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: "2px solid #f0f0f5",
          }}
        >
          <TextField
            placeholder="Search contacts..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ color: "#8b8fa3", fontSize: 14 }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              flex: 1,
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: "8px",
                fontSize: "0.875rem",
                "& fieldset": { borderColor: "#eef0f6" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #eef0f6",
              borderRadius: "8px",
              bgcolor: "#fff",
              px: 1.5,
              height: 40,
              minWidth: 200,
            }}
          >
            <FontAwesomeIcon
              icon={faFilter}
              style={{ color: "#8b8fa3", marginRight: 8, fontSize: 13 }}
            />
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              displayEmpty
              variant="standard"
              disableUnderline
              fullWidth
              sx={{ fontSize: "0.875rem", color: "#5a5f7a" }}
              MenuProps={{
                PaperProps: {
                  sx: { borderRadius: "8px", mt: 0.5, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
                },
              }}
            >
              <MenuItem value="">All Employees</MenuItem>
              <MenuItem value="employeeid">Employee ID</MenuItem>
              <MenuItem value="name">Employee Name</MenuItem>
              <MenuItem value="contact number">Contact Number</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="managerID">Manager ID</MenuItem>
              <MenuItem value="managerName">Manager Name</MenuItem>
              <MenuItem value="job location">Job Location</MenuItem>
              <MenuItem value="role">Work Mode</MenuItem>
            </Select>
          </Box>

          <Typography
            sx={{ ml: "auto", fontSize: "0.8125rem", color: "#8b8fa3", fontWeight: 500 }}
          >
            Showing {currentEmployees.length} of {filteredEmployees.length} contacts
          </Typography>
        </Box>

        {/* Content Area */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: "#667eea" }} />
            <Typography sx={{ mt: 2, color: "#8b8fa3", fontSize: "0.875rem" }}>
              Loading contacts...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography sx={{ color: "#EF4444", fontWeight: 600, mb: 1 }}>
              Something went wrong
            </Typography>
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
              {error}
            </Typography>
          </Box>
        ) : currentEmployees.length === 0 ? (
          <EmptyState
            title="No employees found"
            subtitle="No employees match your current filter criteria"
            icon={<FontAwesomeIcon icon={faSearch} />}
          />
        ) : (
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    "Employee ID",
                    "Name",
                    "Contact",
                    "Email",
                    "Manager ID",
                    "Manager",
                    "Location",
                    "Work Mode",
                    "Actions",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        bgcolor: "#f5f7fb",
                        color: "#5a5f7a",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "2px solid #f0f0f5",
                        whiteSpace: "nowrap",
                        py: 1.75,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentEmployees.map((employee) => (
                  <TableRow
                    key={employee._id}
                    sx={{
                      "&:hover": { bgcolor: "#f5f7fb" },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    {/* Employee ID */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#1a1a2e", fontWeight: 500 }}>
                      {employee.employeeid}
                    </TableCell>

                    {/* Name */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#1a1a2e" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            background: "linear-gradient(135deg, #667eea, #764ba2)",
                            color: "#fff",
                            width: 36,
                            height: 36,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {employee.name?.charAt(0).toUpperCase() || "U"}
                        </Avatar>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                          {employee.name}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Contact */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FontAwesomeIcon
                          icon={faPhone}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.contactNumber || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Email */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.email}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Manager ID */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#5a5f7a" }}>
                      {employee.manager || "N/A"}
                    </TableCell>

                    {/* Manager Name */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#5a5f7a" }}>
                      {employee.managerName || "N/A"}
                    </TableCell>

                    {/* Location */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.jobLocation || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Work Mode */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FontAwesomeIcon
                          icon={employee.workMode === "Work from Office" ? faBuilding : faHome}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.workMode === "Work from Office" ? "Office" : "Home"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title="Archive Employee" arrow>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleArchive(employee._id)}
                          startIcon={
                            <FontAwesomeIcon icon={faArchive} style={{ fontSize: 12 }} />
                          }
                          sx={{
                            borderRadius: "8px",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            textTransform: "none",
                            borderColor: "#eef0f6",
                            color: "#EF4444",
                            px: 1.5,
                            "&:hover": {
                              borderColor: "#EF4444",
                              bgcolor: "#FEF2F2",
                            },
                          }}
                        >
                          Archive
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 2,
              borderTop: "1px solid #eef0f6",
            }}
          >
            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>
              Page {currentPage} of {totalPages}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                startIcon={
                  <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 11 }} />
                }
                sx={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  textTransform: "none",
                  borderColor: "#eef0f6",
                  color: "#5a5f7a",
                  px: 2,
                  "&:hover": { borderColor: "#CBD5E1", bgcolor: "#f5f7fb" },
                  "&.Mui-disabled": {
                    borderColor: "#f0f2f8",
                    color: "#CBD5E1",
                  },
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                endIcon={
                  <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                }
                sx={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  textTransform: "none",
                  borderColor: "#eef0f6",
                  color: "#5a5f7a",
                  px: 2,
                  "&:hover": { borderColor: "#CBD5E1", bgcolor: "#f5f7fb" },
                  "&.Mui-disabled": {
                    borderColor: "#f0f2f8",
                    color: "#CBD5E1",
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </PageContainer>
  );
};

export default EmployeeContactList;
